import React, { useMemo } from 'react'
import { Form, Field } from 'react-final-form'
import { useSelector } from 'react-redux'
import Spinner from 'react-spinkit'
import NormalLayoutContainer from '../shared/NormalLayoutContainer'
import PageTitle from '../../components/page-title'
import CalenderContainer from '../../components/calender-container'
import { timeOptions } from '../../components/TimeInput'
import UnderlineSelect from '../../components/underline-select'
import { CheckboxInput } from '../../components/CheckboxInput'
import { dayOptions } from '../../components/create-ticket-recurring/utils'
import QUERY_KEYS from '../../graphql/queryKeys'
import useQuery from '../../hooks/useQuery'
import useMutation from '../../hooks/useMutation'
import onlineScheduleCustomizationsQuery from '../../graphql/queries/onlineScheduleCustomizations'
import updateOnlineScheduleCalendarSettings from '../../graphql/mutations/updateOnlineScheduleCalendarSettings'
import notify from '../../utilities/notify'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import styles from './index.module.scss'
import BlackoutDatesSection from '../../components/blackout-dates'

const DAY_FIELDS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']

function mapSettingsToForm (settings = {}) {
  const form = {
    cutoffTime: settings.cutoffTime ?? null,
    blackoutDates: (settings.blackoutDates ?? []).map(({ date }) => ({ date }))
  }

  DAY_FIELDS.forEach(day => {
    form[day] = (settings.serviceDays ?? []).includes(day)
  })

  return form
}

function mapFormToInput (values, haulerId) {
  return {
    haulerId,
    settings: {
      cutoffTime: values.cutoffTime,
      serviceDays: DAY_FIELDS.filter(day => values[day]),
      blackoutDates: (values.blackoutDates ?? []).map(({ date }) => ({ date }))
    }
  }
}

export default function OnlineSchedulingCalender () {
  const { user } = useSelector(({ user }) => ({ user: user.user }))

  const { data, isFetching } = useQuery(
    [QUERY_KEYS.onlineScheduleCustomizations, { haulerId: user.haulerId }],
    onlineScheduleCustomizationsQuery,
    {
      enabled: Boolean(user?.haulerId),
      onError: (err) => captureErrorAndNotify(err, 'Failed to load calendar settings'),
      placeholderData: { onlineScheduleCustomizations: { defaults: {}, savedResults: null } }
    }
  )

  const { mutateAsync: saveSettings, isLoading: isSaving } = useMutation(
    updateOnlineScheduleCalendarSettings,
    {
      onSuccess: () => notify('success', 'Calendar settings updated'),
      onError: (err) => captureErrorAndNotify(err, 'Failed to save calendar settings')
    }
  )

  const calendarSettings = useMemo(() => {
    const customizations = data?.onlineScheduleCustomizations
    return customizations?.savedResults?.calendarSettings ?? customizations?.defaults?.calendarSettings ?? {}
  }, [data])

  const initialValues = useMemo(
    () => mapSettingsToForm(calendarSettings),
    [calendarSettings]
  )

  function validate (values) {
    const errors = {}
    if (!values.cutoffTime) errors.cutoffTime = 'Required'
    if (!DAY_FIELDS.some(day => values[day])) {
      errors.serviceDays = 'Select at least one day'
    }
    return errors
  }

  async function onSubmit (values, form) {
    await saveSettings(mapFormToInput(values, user.haulerId))
    form.initialize(values)
  }

  if (isFetching) return <Spinner name='circle' fadeIn='none' />

  return (
    <NormalLayoutContainer showBackLink>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, invalid, pristine, form, values, errors }) => (
          <form onSubmit={handleSubmit}>
            <div className={styles.header}>
              <PageTitle>Calendar Settings</PageTitle>
              <div className={styles.headerBtns}>
                <button
                  type='submit'
                  className='dis-btn dis-btn-lg dis-btn-primary'
                  disabled={pristine || invalid || isSaving}>
                  <span>Save Changes</span>
                  <i className='material-icons dis-btn-icon'>check</i>
                </button>
              </div>
            </div>

            <div className={styles.container}>
              <CalenderContainer title='Cutoff Time'>
                <p className={styles.description}>
                  Set the time after which next-day scheduling is no longer available.
                </p>
                <div className={styles.cutoffTimeField}>
                  <Field name='cutoffTime'>
                    {({ input, meta }) => (
                      <>
                        <label className='control-label' htmlFor='cutoffTime'>Cutoff Time</label>
                        <UnderlineSelect
                          inputId='cutoffTime'
                          placeholder='Select Time'
                          options={timeOptions}
                          defaultValue={timeOptions.find(option => option.value === input.value) || null}
                          onChange={option => input.onChange(option?.value ?? null)}
                          isSearchable
                          closeMenuOnSelect
                        />
                        {meta.error && <span className='text-danger'>{meta.error}</span>}
                      </>
                    )}
                  </Field>
                </div>
              </CalenderContainer>

              <CalenderContainer title='Days Of Week Available'>
                <p className={styles.description}>
                  Select the days of the week on which the hauler provides service (Monday-Sunday).
                </p>
                <div className={styles.dayCheckboxes}>
                  {dayOptions.map(({ value, label }) => (
                    <Field
                      key={value}
                      type='checkbox'
                      name={value}
                      label={label}
                      component={CheckboxInput}
                    />
                  ))}
                </div>
                {errors?.serviceDays && (
                  <h5 className='text-danger'>{errors.serviceDays}</h5>
                )}
              </CalenderContainer>

              <CalenderContainer title='Exclude Specific Dates'>
                <p className={styles.description}>
                  Select individual dates when service is unavailable, such as holidays or planned closures.
                </p>
                <p className={styles.description}>
                  These dates are manually configured each year and do not recur automatically.
                </p>
                <p className={styles.subdescription}>
                  Exclude Specific Date
                </p>
                <BlackoutDatesSection form={form} values={values} />
              </CalenderContainer>
            </div>
          </form>
        )}
      />
    </NormalLayoutContainer>
  )
}
